import { AbstractAnalyticsTracker } from '@inter-ikea-kompis/analytics';
import { IpexMomentEnum } from '@insights/insights-data-provider';
const moduleId = 'product_card';
/**
 * Tracks events from Product Card and sends them to a reporter.
 *
 * @event product_card_carousel_change Triggered when a carousel slide should be changed. Only available when layout is set to "information".
 * @event product_card_carousel_show_all_thumbnails Triggered when the show all thumbnails button is clicked in the carousel. Only available when layout is set to "information".
 * @event product_card_product_variant_click Triggered when a product variant thumbnail is clicked. Only available when layout is set to "gallery".
 * @event product_card_variant_show_more_click Triggered when the show more variants button is clicked. Only available when layout is set to "gallery".
 * @event product_card_info_icon_button_click Triggered when the info icon button is clicked. Only available when layout is set to "list".
 * @event product_card_selected_product Triggered when the user tabs to the card and selects it by pressing the Enter key. Only available when layout is set to "list".
 * @event product_card_store_selector_change Triggered when the user selects a store in the store selector. Only available when layout is set to "information".
 * @event product_card_wood_declaration_expand Triggered when the user clicks on the wood declaration accordion. Only available when layout is set to "information".
 */
export default class ProductCardTracker extends AbstractAnalyticsTracker {
    constructor(options) {
        super({ moduleId, ...options });
    }
    /**
     * Call to report when the image is changed the carousel through either clicking on the thumbnail or arrows in the carousel in Product Card Information.
     *
     * @param carouselChangeDetails ...
     * @param carouselChangeDetails.carouselSelectedIndex The selected index of the carousel.
     * @param carouselChangeDetails.type How the change was made, either from thumbnail or navigation arrows in the carousel.
     */
    sendCarouselChange(carouselChangeDetails) {
        this.getReporter().report({
            event: 'product_card_carousel_change',
            ipexMoment: IpexMomentEnum.whatIsMe,
            payload: carouselChangeDetails
        });
    }
    /**
     * Call to report when the button for showing all/show less thumbnails is clicked in Product Card Information.
     *
     * @param carouselShowAll Indicating whether or not to show all thumbnails.
     */
    sendCarouselShowAllThumbnails(carouselShowAll) {
        this.getReporter().report({
            event: 'product_card_carousel_show_all_thumbnails',
            ipexMoment: IpexMomentEnum.whatIsMe,
            payload: { carouselShowAll }
        });
    }
    /**
     * Call to report when clicking on a product variant in Product Card Gallery.
     *
     * @param selectedProduct RuItemNo of the product.
     */
    sendProductVariantClick(selectedProduct) {
        this.getReporter().report({
            event: 'product_card_product_variant_click',
            ipexMoment: IpexMomentEnum.whatIsMe,
            payload: { selectedProduct }
        });
    }
    /**
     * Call to report when the button for showing more product variants is clicked in Product Card Gallery.
     *
     * @param product RuItemNo of the product.
     */
    sendProductVariantShowMoreClick(product) {
        this.getReporter().report({
            event: 'product_card_variant_show_more_click',
            ipexMoment: IpexMomentEnum.whatIsMe,
            payload: { product }
        });
    }
    /**
     * Call to report when the info icon button is clicked in the Product Card List.
     *
     * @param product RuItemNo of the product.
     */
    sendInfoIconButtonClick(product) {
        this.getReporter().report({
            event: 'product_card_info_icon_button_click',
            ipexMoment: IpexMomentEnum.whatIsMe,
            payload: { product }
        });
    }
    /**
     * Call to report when selecting a product in Product Card List.
     *
     * @param selectedState The selected state of the product card, either 'checkbox' or 'unselected'.
     */
    sendSelectedProduct(selectedState) {
        this.getReporter().report({
            event: 'product_card_selected_product',
            ipexMoment: IpexMomentEnum.makeItMe,
            payload: { selectedState }
        });
    }
    /**
     * Call to report when the store selector is changed in Product Card Information.
     *
     * @param storeSelectorChangeDetails ...
     * @param storeSelectorChangeDetails.storeId The storeId of the chosen store.
     * @param storeSelectorChangeDetails.product RuItemNo of the product.
     */
    sendStoreSelectorChange(storeSelectorChangeDetails) {
        this.getReporter().report({
            event: 'product_card_store_selector_change',
            ipexMoment: IpexMomentEnum.showMe,
            payload: storeSelectorChangeDetails
        });
    }
    /**
     * Call to report when the wood declaration accordion has been pressed in Product Card Information.
     *
     * @param expand Indicating whether or not the wood declaration accordion was pressed.
     */
    sendWoodDeclarationExpand(expand) {
        this.getReporter().report({
            event: 'product_card_wood_declaration_expand',
            ipexMoment: IpexMomentEnum.whatIsMe,
            payload: { expand }
        });
    }
}
//# sourceMappingURL=ProductCardTracker.js.map